import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Swap bathroom faucet and both supply lines (customer supplies the faucet)', amount: '$185' },
  { item: 'Hallway: patch two doorknob holes in drywall, texture to match, prime and paint', amount: '$240' },
  { item: 'Living room: hang three floating shelves and a TV wall mount, find studs, hide cables in a surface raceway', amount: '$210' },
  { item: 'Optional: re-hang and adjust the sticking back door, new strike plate', amount: '$95' },
];

const faq = [
  {
    q: 'What do I do when the customer adds jobs while I am already there?',
    a: 'Finish what was signed, then start a new request for the same client in Scopie and quote the extra tasks on it. The customer signs that second quote on its own, so the first one stays exactly as agreed and nobody argues about the extra work at invoice time.',
  },
  {
    q: 'Should a handyman charge a minimum or a trip fee?',
    a: 'Many do, because a 20-minute job still costs the drive and the setup. If you charge one, write it into your terms so it appears on every quote, rather than explaining it at the door.',
  },
  {
    q: 'How do I list materials the customer is buying?',
    a: 'Say it on the line itself, for example "customer supplies the faucet", and add a clause about what happens if the part is wrong or missing on the day.',
  },
  {
    q: 'Does Scopie sync with QuickBooks?',
    a: 'No, not today. Your quotes, invoices and client history live in Scopie, but there is no QuickBooks or accounting sync.',
  },
];

const page: IndustryPage = {
  slug: 'handyman',
  order: 7,
  group: 'Home services',
  tile: 'handyman',
  path: '/for/handyman',
  primaryKeyword: 'handyman business software',
  secondaryKeywords: ['handyman quote template', 'handyman estimate app', 'how to price handyman jobs'],
  title: 'Handyman Software for Quotes and Repeat Customers | Scopie', // 58
  metaDescription:
    'Turn a customer\'s to-do list into a quote with a line per task, settle trip fees and parts in your terms, and keep each client\'s job history in one place.', // 155
  h1: 'Handyman quotes for the whole to-do list',
  answerFirst:
    'Scopie turns a customer\'s to-do list into a handyman quote with one line per task, so they can see what each repair costs and say yes to the lot. Your trip fee, parts policy and minimum sit in saved clauses. Repeat customers keep their history, so the next list is quicker to price.',
  exampleQuote: { label: 'Sample handyman quote', lines, total: '$730', depositNote: 'Trip fee and minimum charge come from the handyman\'s saved clauses.' },
  blocks: [
    {
      type: 'list',
      id: 'the-list',
      h2: 'Customers call with a list, not a single job',
      style: 'bullets',
      intro: 'A typical first call sounds like this, and every item needs its own price:',
      items: [
        { text: 'The bathroom faucet drips and the new one is already in the box.' },
        { text: 'There are two holes in the hallway wall from the doorstop that came off.' },
        { text: 'Shelves and the TV need to go up before the in-laws visit.' },
        { text: 'The back door sticks every summer.' },
      ],
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'One visit, four tasks, one quote',
      label: 'Sample quote for a half-day visit',
      lines,
      total: '$730',
      totalLabel: 'Total with the door adjustment',
      note: 'Example figures. The door is marked optional, so the customer can tick it or leave it for another day.',
    },
    {
      type: 'text',
      id: 'per-task',
      h2: 'Why a line per task wins more lists',
      lead: '"Can you do the whole lot for less?"',
      paragraphs: [
        'A single total for four jobs invites haggling over all of them. When each task has its own price, the customer trims the list instead of your rate: they keep the faucet and the wall, and push the shelves to next month.',
        'Lines also protect you later. If the customer asks why the garage light was not looked at, the quote they signed shows it was never on it.',
      ],
    },
    {
      type: 'text',
      id: 'extra-work',
      h2: '"While you\'re here" work gets its own request',
      paragraphs: [
        'The moment you finish the faucet, the customer remembers the running toilet. Do not fold it into the signed quote. Start a new request for the same client, quote the toilet on it, and let them sign that separately. It takes a minute on your phone, and the first quote stays clean.',
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Five ways handyman quotes leak money',
      style: 'mistakes',
      items: [
        { title: 'No trip or minimum charge in writing', text: 'Explaining it at the door feels like a surprise fee. On the quote, it is just your policy.' },
        { title: 'Parts assumed', text: 'Say who buys the part and what happens if the customer\'s part is the wrong size.' },
        { title: 'Hidden conditions', text: 'Rot behind a tile or a seized shutoff valve changes the job. Say you will stop and quote it.' },
        { title: 'Small jobs priced from memory', text: 'Save your common tasks as items so a doorknob hole costs the same every time.' },
        { title: 'Old quotes with no end date', text: 'A price from last spring should not be binding this fall.' },
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Handyman quoting, answered', items: faq },
    {
      type: 'text',
      id: 'regulars',
      h2: 'Regulars, history and the next list',
      paragraphs: [
        'Your best customers call every month or two. Each one stays in your client list with every quote and invoice, so you can see the shelf brackets you used last time and price the next list in the same terms. When the work is done, share the invoice link from your phone before you leave the driveway.',
      ],
    },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/painting', anchor: 'Scopie for painting contractors', status: 'live' },
    { href: '/for/flooring', anchor: 'Scopie for flooring contractors', status: 'live' },
    { href: '/templates/handyman-quote-template', anchor: 'free handyman quote template', status: 'live' },
  ],
  cta: {
    heading: 'Price the next to-do list in minutes',
    text: 'Save your everyday tasks and your trip fee once, and Scopie fills them into every handyman quote.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
